import { useEffect, useState } from 'react';
import { StoreProvider, useStore } from './state/store';
import { BottomNav, SideRail, type Tab } from './components/Nav';
import { DashboardPage } from './pages/Dashboard';
import { UpdatePage } from './pages/Update';
import { HistoryPage } from './pages/History';
import { ProjectionPage } from './pages/Projection';
import { AccountsPage } from './pages/Accounts';
import { AssumptionsPage } from './pages/Assumptions';
import { ScenariosPage } from './pages/Scenarios';
import { InvestmentsPage } from './pages/Investments';
import { CohortsPage } from './pages/Cohorts';
import { SettingsPage } from './pages/Settings';
import { PrivacyPage } from './pages/Privacy';
import { GlossaryPage } from './pages/Glossary';
import { WelcomePage } from './pages/Welcome';
import { GlossaryProvider, useGlossary } from './glossary/GlossaryContext';
import { GlossaryPanel } from './components/GlossaryPanel';

export default function App() {
  return (
    <StoreProvider>
      <GlossaryProvider>
        <Shell />
        <GlossaryPanel />
      </GlossaryProvider>
    </StoreProvider>
  );
}

const VALID_TABS: Tab[] = [
  'dashboard',
  'projection',
  'scenarios',
  'investments',
  'cohorts',
  'update',
  'history',
  'accounts',
  'assumptions',
  'settings',
  'privacy',
  'glossary',
];

function Shell() {
  const {
    ready,
    needsOnboarding,
    activeProfile,
    profiles,
    switchProfile,
    startOnboarding,
  } = useStore();
  const { state: glossaryState } = useGlossary();
  const [tab, setTab] = useState<Tab>(() => {
    const h = window.location.hash.slice(1) as Tab;
    if (VALID_TABS.includes(h)) return h;
    return 'dashboard';
  });

  useEffect(() => {
    const on = () => {
      const h = window.location.hash.slice(1) as Tab;
      if (VALID_TABS.includes(h) && h !== tab) setTab(h);
    };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, [tab]);

  if (!ready) return <BootScreen />;

  // Welcome / onboarding overlay. Rendered instead of the shell — no side
  // rail, no bottom nav, no dashboard chrome — so a first-time visitor's
  // first pixel is a friendly intro, not a stranger's plan of record.
  if (needsOnboarding) {
    return <WelcomePage canDismiss={profiles.length > 0} />;
  }

  const navTab = isPrimary(tab) ? tab : mapToPrimary(tab);
  const isDemo = activeProfile === 'demo';
  const hasMainProfile = profiles.some(p => p.name === 'main');

  // Publish a CSS variable so sticky elements know how much top chrome to
  // offset for. The demo banner is the only top chrome.
  const topChromeHeight = isDemo ? 'calc(28px + env(safe-area-inset-top))' : '0px';
  const shellStyle: React.CSSProperties = ({
    ['--top-chrome' as never]: topChromeHeight,
  } as React.CSSProperties);

  const go = (t: string) => {
    if (!VALID_TABS.includes(t as Tab)) return;
    setTab(t as Tab);
    window.location.hash = t;
  };

  // When the glossary panel is open on lg+ (where it's a fixed gutter on the
  // right), reserve room so the page content doesn't slide under it. Mobile
  // / tablet layouts are overlay-style, so no reservation needed.
  const glossaryOpenLg = glossaryState.currentId != null;
  const shellPadRight = glossaryOpenLg ? 'lg:pr-[380px]' : '';

  return (
    <div className={`min-h-screen lg:pl-16 ${shellPadRight}`} style={shellStyle}>
      {isDemo && (
        <DemoBanner
          mode={hasMainProfile ? 'switch' : 'onboard'}
          onSwitchToMain={() => switchProfile('main')}
          onStartOwnPlan={startOnboarding}
        />
      )}
      <SideRail tab={navTab} onChange={go} />
      {tab === 'dashboard' && <DashboardPage onGoTo={go} />}
      {tab === 'projection' && <ProjectionPage onGoTo={go} />}
      {tab === 'scenarios' && <ScenariosPage onGoTo={go} />}
      {tab === 'investments' && <InvestmentsPage />}
      {tab === 'cohorts' && <CohortsPage />}
      {tab === 'update' && <UpdatePage onDone={() => go('dashboard')} />}
      {tab === 'history' && <HistoryPage onGoTo={go} />}
      {tab === 'settings' && <SettingsPage onNav={go} />}
      {tab === 'accounts' && <AccountsPage onBack={() => go('settings')} />}
      {tab === 'assumptions' && <AssumptionsPage onBack={() => go('settings')} />}
      {tab === 'privacy' && <PrivacyPage onBack={() => go('settings')} />}
      {tab === 'glossary' && <GlossaryPage onBack={() => go('settings')} />}
      <BottomNav tab={navTab} onChange={go} />
    </div>
  );
}

interface DemoBannerProps {
  /** Whether the user already has a `main` profile to switch back to, or
   *  needs to be routed into onboarding to create one. */
  mode: 'switch' | 'onboard';
  onSwitchToMain: () => void | Promise<void>;
  onStartOwnPlan: () => void;
}

function DemoBanner({ mode, onSwitchToMain, onStartOwnPlan }: DemoBannerProps) {
  return (
    <div
      role="status"
      className="sticky top-0 z-30 flex items-center justify-center gap-2 bg-amber-500 px-4 py-1 text-[11px] font-semibold text-amber-950 shadow-sm"
      style={{ paddingTop: 'calc(0.25rem + env(safe-area-inset-top))' }}
    >
      <span aria-hidden>●</span>
      <span>Demo profile — fake data · safe for screenshots</span>
      {mode === 'switch' ? (
        <button
          type="button"
          onClick={() => {
            void onSwitchToMain();
          }}
          className="ml-2 rounded-md bg-amber-950/10 px-2 py-0.5 hover:bg-amber-950/20"
        >
          Switch to main →
        </button>
      ) : (
        <button
          type="button"
          onClick={onStartOwnPlan}
          className="ml-2 rounded-md bg-amber-950/10 px-2 py-0.5 hover:bg-amber-950/20"
        >
          Start your own plan →
        </button>
      )}
    </div>
  );
}

function BootScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-accent-500 border-r-transparent" />
        <div className="mt-3 text-sm text-ink-500">Loading…</div>
      </div>
    </div>
  );
}

function isPrimary(t: Tab): boolean {
  return (
    t === 'dashboard' ||
    t === 'projection' ||
    t === 'scenarios' ||
    t === 'investments' ||
    t === 'cohorts' ||
    t === 'update' ||
    t === 'history' ||
    t === 'settings'
  );
}

function mapToPrimary(t: Tab): Tab {
  if (
    t === 'accounts' ||
    t === 'assumptions' ||
    t === 'privacy' ||
    t === 'glossary'
  )
    return 'settings';
  return 'dashboard';
}
